
import React, { useState } from 'react';
import { format, startOfWeek } from 'date-fns';
import { Button } from "@/components/ui/button";
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface WeekVisibilityToggleProps {
  date: Date;
  teamId: string;
  isVisible: boolean;
  onVisibilityChange: (date: Date, isVisible: boolean) => Promise<boolean>;
}

const WeekVisibilityToggle: React.FC<WeekVisibilityToggleProps> = ({ 
  date, 
  teamId,
  isVisible,
  onVisibilityChange 
}) => {
  const { t } = useLanguage();
  const [updating, setUpdating] = useState(false);
  const weekStart = startOfWeek(date, { weekStartsOn: 1 });
  
  const toggleVisibility = async () => {
    if (!teamId || updating) return;
    
    setUpdating(true);
    try {
      const newVisibility = !isVisible;
      
      console.log('WeekVisibilityToggle - Toggling visibility:', {
        weekStart: format(weekStart, 'yyyy-MM-dd'),
        currentState: isVisible,
        newState: newVisibility
      });
      
      const success = await onVisibilityChange(weekStart, newVisibility);
      
      if (!success) {
        console.error('WeekVisibilityToggle - Failed to update visibility');
        // The parent component should handle the error state, so we don't need to do anything here
      } else {
        console.log('WeekVisibilityToggle - Successfully updated visibility');
      }
    } catch (error) {
      console.error('WeekVisibilityToggle - Error during toggle:', error);
    } finally {
      setUpdating(false);
    }
  };
  
  const tooltipText = isVisible
    ? t("weekVisible") || "This week's schedule is visible to guards"
    : t("weekHidden") || "This week's schedule is hidden from guards";
  
  console.log('WeekVisibilityToggle - Rendering with:', {
    weekStart: format(weekStart, 'yyyy-MM-dd'),
    isVisible,
    updating
  });
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={`h-7 w-7 p-1 ${
              isVisible 
                ? 'text-green-600 hover:text-green-700 hover:bg-green-100' 
                : 'text-red-500 hover:text-red-600 hover:bg-red-100'
            }`}
            onClick={toggleVisibility}
            disabled={updating}
          >
            {updating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : isVisible ? (
              <Eye className="h-4 w-4" />
            ) : (
              <EyeOff className="h-4 w-4" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>{tooltipText}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default WeekVisibilityToggle;
